import React from "react";
import {
  DepressionContentPage,
  ContentSection,
  FAQItem,
} from "@/components/DepressionContentPage";
import { RelatedLink } from "@/components/RelatedPages";
import { Phone } from "lucide-react";
import BenefitsCheckForm from "@/components/BenefitsCheckForm";

/* ── Reusable CTA block with phone link ── */
const CtaBlock: React.FC<{
  heading?: string;
  text?: string;
  primaryCta: string;
  note?: string;
}> = ({ heading, text, primaryCta, note }) => (
  <div className="my-6 rounded-2xl border-l-4 border-accent bg-card p-6 sm:p-8 shadow-soft space-y-3">
    {heading && (
      <p className="font-headings text-lg sm:text-xl font-bold text-primary">
        {heading}
      </p>
    )}
    {text && (
      <p className="text-foreground/80 text-base leading-relaxed">{text}</p>
    )}
    <div className="flex flex-col sm:flex-row gap-3 pt-2">
      <a
        href="/#consultation"
        className="inline-flex items-center justify-center rounded-lg bg-gradient-accent px-6 py-3 text-base font-semibold text-accent-foreground shadow-glow hover:opacity-90 transition-opacity"
      >
        {primaryCta}
      </a>
      <a
        href="tel:9548279793"
        className="inline-flex items-center justify-center rounded-lg border-2 border-primary px-6 py-3 text-base font-semibold text-primary hover:bg-primary/5 transition-colors"
      >
        <Phone className="h-4 w-4 mr-2" /> Call (954) 827-9793
      </a>
    </div>
    {note && <p className="text-sm text-muted-foreground pt-1">{note}</p>}
  </div>
);

const seo = {
  title: "Find Out What Your Plan Covers — Free | Neuro Wellness TMS",
  description:
    "Find out what your plan covers for SAINT TMS — free. We verify your benefits at no cost and tell you exactly where you stand. Self-pay options available.",
  canonical: "/saint-tms-insurance-coverage",
  keywords:
    "SAINT TMS insurance coverage, does insurance cover SAINT TMS, SAINT TMS cost, TMS insurance verification, SAINT TMS prior authorization",
};

const sections: ContentSection[] = [
  {
    id: "cta-above-fold",
    variant: "text",
    body: <BenefitsCheckForm source="saint-insurance-coverage-top" />,
  },
  {
    id: "saint-explainer-video",
    variant: "video",
    videoVariant: "cost",
  },
  {
    id: "what-determines-coverage",
    heading: "What determines whether your plan covers SAINT",
    intro:
      "Most insurers evaluate accelerated TMS the same way they evaluate standard TMS, with a few extra questions. The factors that matter:",
    variant: "text",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-3">
          <li>
            <strong>Your diagnosis.</strong> Coverage is generally tied to a
            diagnosis of major depressive disorder. Some plans extend to
            treatment-resistant depression specifically.
          </li>
          <li>
            <strong>Your treatment history.</strong> Nearly every plan requires
            that you have tried and not responded to a certain number of
            antidepressant medications — commonly two to four — and often a
            trial of psychotherapy.
          </li>
          <li>
            <strong>Whether your plan covers TMS at all.</strong> Most major
            commercial plans do. Coverage for the accelerated protocol
            specifically varies more.
          </li>
          <li>
            <strong>In-network vs out-of-network.</strong> This changes your
            out-of-pocket cost significantly, not whether you qualify
            clinically.
          </li>
          <li>
            <strong>Prior authorization.</strong> Almost always required. This
            is paperwork, not a barrier — it just takes time.
          </li>
        </ul>
        <p className="mt-4">
          Insurance coverage for SAINT® is currently limited and varies by plan.
          We verify your benefits at no cost and tell you exactly where you
          stand. Self-pay options are available.
        </p>
      </>
    ),
  },
  {
    id: "how-benefits-check-works",
    heading: "How the benefits check works",
    variant: "steps",
    steps: [
      {
        title: "You send us your insurance information",
        body: "Member ID, plan name, date of birth. Two minutes on the phone or through the form.",
      },
      {
        title: "We call your insurer",
        body: "We ask the specific questions that matter: is TMS a covered benefit, does the accelerated protocol qualify, what are the prior-authorization requirements, what is your deductible and coinsurance.",
      },
      {
        title: "We tell you what we found",
        body: "In plain language — what is covered, what you would owe, and what the approval process looks like.",
      },
      {
        title: "You decide",
        body: "If the numbers don't work, you have lost nothing but a phone call.",
      },
    ],
  },
  {
    id: "cta-mid-page",
    variant: "text",
    body: <BenefitsCheckForm source="saint-insurance-coverage-mid" />,
  },
  {
    id: "cost-without-insurance",
    heading: "What SAINT costs without insurance",
    variant: "text",
    body: (
      <>
        <p>
          The total course of treatment includes the fMRI mapping session, all
          treatment sessions across the five days, and follow-up. Financing and
          payment plans are available, and the cost is generally comparable to a
          six-week course of standard TMS — but completed in one week rather
          than spread over six. We will give you a specific number based on your
          situation before you commit to anything.
        </p>
      </>
    ),
  },
  {
    id: "approval-timeline",
    heading: "How long does approval take?",
    variant: "text",
    body: (
      <p>
        Typically two to four weeks. People planning a week away from work need
        a real timeline, and we would rather give you an honest one than an
        optimistic one.
      </p>
    ),
  },
];

const faq: FAQItem[] = [
  {
    question: "Is SAINT TMS covered by insurance?",
    answer:
      "Many commercial insurance plans cover TMS therapy for treatment-resistant depression, and coverage for accelerated protocols like SAINT has been expanding. Whether your specific plan covers it depends on your diagnosis, your prior treatment history, and your plan's medical policy. We verify benefits at no cost before you commit to anything.",
  },
  {
    question: "Does Blue Cross Blue Shield cover TMS therapy?",
    answer:
      "Many Blue Cross Blue Shield plans include TMS therapy as a covered benefit for major depressive disorder when medication trials have not worked. Requirements vary by state and by plan. Coverage for accelerated protocols like SAINT® is currently limited and varies by plan. We can check your specific plan to tell you what it covers.",
  },
  {
    question: "Do I need prior authorization for SAINT TMS?",
    answer:
      "Yes, in nearly all cases. Your insurer will want documentation of your diagnosis and your history of medication trials. We handle the submission as part of getting you started.",
  },
  {
    question: "What if my insurance denies coverage?",
    answer:
      "Denials are often appealable, particularly when the denial is based on incomplete documentation of prior treatment. We can also discuss self-pay options. A denial is not the end of the conversation.",
  },
  {
    question: "How much does SAINT cost out of pocket?",
    answer:
      "The cost varies based on your plan and benefits. We verify your benefits at no cost so you know your specific out-of-pocket amount before you commit.",
  },
  {
    question: "Is SAINT covered by Medicare or Medicaid?",
    answer:
      "Medicare Part B may cover TMS for major depressive disorder when medical necessity criteria are met. Medicaid coverage varies by state. We can verify your specific plan to confirm.",
  },
];

const relatedLinks: RelatedLink[] = [
  {
    title: "How Much Does SAINT TMS Cost?",
    href: "/learn/saint-tms-cost",
    description: "What SAINT costs and what drives your out-of-pocket expense.",
  },
  {
    title: "Find a Verified SAINT Provider",
    href: "/find-a-verified-saint-provider",
    description: "How to verify a legitimate SAINT provider and what to ask.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How SAINT works and who it may be for.",
  },
  {
    title: "SAINT vs Traditional TMS",
    href: "/learn/saint-vs-traditional-tms",
    description: "An honest side-by-side comparison.",
  },
  {
    title: "SAINT® TMS 5-Day Treatment",
    href: "/saint-tms-5-day-depression-treatment",
    description: "Our main SAINT® treatment page.",
  },
  {
    title: "SAINT® TMS Overview",
    href: "/saint",
    description: "Learn more about SAINT® at Neuro Wellness.",
  },
];

const SaintTmsInsuranceCoverage = () => (
  <DepressionContentPage
    seo={seo}
    badge="Free Insurance Verification"
    heroTitle="Find out what your plan covers — free"
    heroSubtitle="Insurance coverage for SAINT® is currently limited and varies by plan. We verify your benefits at no cost and tell you exactly where you stand. Self-pay options are available."
    heroCtaPrimary="Start My Free Benefits Check"
    heroForm
    heroFormSource="saint-tms-insurance-coverage"
    sections={sections}
    faq={faq}
    faqHeading="Frequently Asked Questions"
    relatedLinks={relatedLinks}
    relatedHeading="Explore Related SAINT Resources"
    finalCtaHeading="Find out what your plan covers — free"
    finalCtaText="One phone call and you'll know where you stand."
  />
);

export default SaintTmsInsuranceCoverage;
